// TODO: migrate from legacy CI tooling: resolve ts-* and/or eslint-* directives below (including inline comments)
/* eslint-disable @typescript-eslint/ban-ts-comment, @typescript-eslint/no-explicit-any, @typescript-eslint/no-misused-promises, @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/prefer-promise-reject-errors, no-async-promise-executor */

import { queryOptions } from "@tanstack/react-query";
import moment from "moment";
import { AerialPlotResponse } from "./types";

import type {
	CreateDataLayerRequest,
	CreateDataLayerUploadRequest,
	DataLayer,
	DataLayerDropdownInfo,
	DataLayerOptions,
	DataLayerUpload,
	FieldWithBoundaryAndPlots,
	PlotResultsInfo,
	UUID,
	VectorDataLayerRequest,
	VectorDataLayerUploadPreprocResult,
	VectorDataLayerUploadStdResult,
} from "./types/dataLayerTypes";

type AccessTokenProvider = () => Promise<string>;

// @ts-expect-error
const apApiBaseUrl: string = process.env.aerialPlotConnections.aerialPlotApi.baseUrl;
// @ts-expect-error
const apDataPlatformBaseUrl: string = process.env.aerialPlotConnections.aerialDataPlatformApi.baseUrl;

/**
 * Aerialplot API path prefix
 */
const DATA_LAYERS_PATH = "api/v1/DataLayers/";

/**
 * Aerialplot API path prefix
 */
const FACTORS_PATH = "api/v1/Factors/";

/**
 * Aerialplot Data Platform API path prefix
 */
const DP_DATA_LAYERS_PATH = "DataLayers/";

/**
 * Error class for returning server errors and API validation error message back to component level.
 */
export class ApiError extends Error {
	constructor(
		message: string,
		public status: number,
		public body?: AerialPlotResponse<any, any>,
	) {
		super(message);
		this.name = "ApiError";
	}
}

/**
 * Helper fn for fetch of a SAS url from Azure File Share, includes workaround for response of
 * ConditionHeadersNotSupported (prevent browser from attempting cache revalidation). Throws Error
 * on failure, to be react-query compatible.
 */
async function fetchFromAzureFileShare(url: string): Promise<Response> {
	const fetchOptions: RequestInit = { cache: "no-store" };
	const response = await fetch(url, fetchOptions);
	if (!response.ok) {
		// handle HTTP unsuccessful response codes (>299)
		const msg = await response.text();
		console.error(response, msg);
		throw new Error(msg);
	}
	return response;
}

// ===== aerialPlot API - DataLayers-Related Endpoints =====

/**
 * Query options for GetDataLayerOptions. (DataLayerGroups and DataLayerTypes).
 * Leverages queryOptions per react-query guidelines
 * https://tanstack.com/query/latest/docs/framework/react/guides/query-options
 */
function dataLayerQueryOptions(getAccessToken: AccessTokenProvider, clientId: UUID) {
	return queryOptions<DataLayerOptions, Error, DataLayerOptions, ["GetDataLayerOptions", UUID]>({
		enabled: !!clientId,
		queryKey: ["GetDataLayerOptions", clientId],
		queryFn: async () => {
			const accessToken = await getAccessToken();
			const responseModel = await _getDataLayerOptions(accessToken, clientId);
			return responseModel.data;
		},
	});
}

/**
 * Fetch DataLayerOptions. Throws Error on failure, to be react-query compatible.
 */
async function _getDataLayerOptions(
	accessToken: string,
	clientId: UUID,
): Promise<AerialPlotResponse<DataLayerOptions>> {
	const url = `${apApiBaseUrl}${DATA_LAYERS_PATH}GetDataLayerOptions?clientId=${clientId}`;
	const fetchOptions = {
		method: "GET",
		headers: {
			Authorization: `Bearer ${accessToken}`,
		},
	};
	const response = await fetch(url, fetchOptions);
	const body = await response.json();
	if (!response.ok) {
		// handle HTTP unsuccessful response codes (>299)
		const apr: AerialPlotResponse<any> = body;
		throw new ApiError(apr?.statusDescription ?? "GetDataLayerOptions failed", response.status, apr);
	}
	return body as AerialPlotResponse<DataLayerOptions>;
}

/**
 * Query options for GetDataLayerDetails. Leverages queryOptions per react-query guidelines
 * https://tanstack.com/query/latest/docs/framework/react/guides/query-options
 */
function dataLayerDetailsQueryOptions(getAccessToken: AccessTokenProvider, clientId: UUID, dataLayerId?: UUID) {
	return queryOptions<DataLayer, Error, DataLayer, ["GetDataLayerDetails", UUID, UUID | undefined]>({
		enabled: !!dataLayerId,
		queryKey: ["GetDataLayerDetails", clientId, dataLayerId],
		queryFn: async () => {
			const accessToken = await getAccessToken();
			const responseModel = await _getDataLayerDetails(accessToken, clientId, dataLayerId!);
			const dataLayer = responseModel.data;
			dataLayer.dateAndTime = dataLayer.dateAndTime
				? moment.utc(dataLayer.dateAndTime).toDate()
				: dataLayer.dateAndTime;
			return dataLayer;
		},
	});
}

/**
 * Fetch DataLayer details by Id. Throws Error on failure, to be react-query compatible.
 */
async function _getDataLayerDetails(
	accessToken: string,
	clientId: UUID,
	dataLayerId: UUID,
): Promise<AerialPlotResponse<DataLayer>> {
	const url = `${apApiBaseUrl}${DATA_LAYERS_PATH}GetDataLayerDetails/?dataLayerId=${dataLayerId}&clientId=${clientId}`;
	const fetchOptions = {
		method: "GET",
		headers: {
			Authorization: `Bearer ${accessToken}`,
		},
	};
	const response = await fetch(url, fetchOptions);
	const body = await response.json();
	if (!response.ok) {
		// handle HTTP unsuccessful response codes (>299)
		const apr = body as AerialPlotResponse<any>;
		throw new ApiError(apr?.statusDescription ?? "GetDataLayerDetails failed", response.status, apr);
	}
	return body as AerialPlotResponse<DataLayer>;
}

/**
 * Create a new data layer.
 */
async function createDataLayer({
	getAccessToken,
	request,
}: {
	getAccessToken: AccessTokenProvider;
	request: CreateDataLayerRequest;
}): Promise<AerialPlotResponse<DataLayer>> {
	const accessToken = await getAccessToken();
	const url = apApiBaseUrl + DATA_LAYERS_PATH;
	const fetchOptions = {
		method: "POST",
		headers: {
			Authorization: `Bearer ${accessToken}`,
			"Content-Type": "application/json",
		},
		body: JSON.stringify(request),
	};
	const response = await fetch(url, fetchOptions);
	const body = await response.json();
	if (!response.ok) {
		// Throw error on HTTP unsuccessful response codes (>299) per react-query guidelines.
		const apr = body as AerialPlotResponse<any>;
		throw new ApiError(apr?.statusDescription ?? "Create DataLayer failed", response.status, apr);
	}
	return body as AerialPlotResponse<DataLayer>;
}

/**
 * Delete a data layer (database cascades delete to all child tables)
 */
async function deleteDataLayer({
	getAccessToken,
	clientId,
	dataLayerId,
}: {
	getAccessToken: AccessTokenProvider;
	clientId: UUID;
	dataLayerId: UUID;
}): Promise<AerialPlotResponse<null>> {
	const accessToken = await getAccessToken();
	const url = `${apApiBaseUrl}${DATA_LAYERS_PATH}?clientId=${clientId}&dataLayerId=${dataLayerId}`;
	const fetchOptions = {
		method: "DELETE",
		headers: {
			Authorization: `Bearer ${accessToken}`,
			"Content-Type": "application/json",
		},
	};
	const response = await fetch(url, fetchOptions);
	const body = await response.json();
	if (!response.ok) {
		// Throw error on HTTP unsuccessful response codes (>299) per react-query guidelines.
		const apr = body as AerialPlotResponse<any>;
		throw new ApiError(apr?.statusDescription ?? "Delete DataLayer failed", response.status, apr);
	}
	return body as AerialPlotResponse<null>;
}

/**
 * Get list of data layer -> field associations.
 *
 * Leverages queryOptions per react-query guidelines
 * https://tanstack.com/query/latest/docs/framework/react/guides/query-options
 */
function dataLayerFieldAssociationsQueryOptions(
	getAccessToken: AccessTokenProvider,
	clientId: UUID,
	dataLayerId?: UUID,
) {
	return queryOptions<UUID[], Error, UUID[], ["GetDataLayerFieldAssociations", UUID, UUID | undefined]>({
		enabled: !!dataLayerId,
		queryKey: ["GetDataLayerFieldAssociations", clientId, dataLayerId],
		queryFn: async () => {
			const accessToken = await getAccessToken();
			const responseModel = await _getDataLayersFields(accessToken, clientId, dataLayerId!);
			const uuids = responseModel.data;
			return uuids;
		},
	});
}

async function _getDataLayersFields(
	accessToken: string,
	clientId: UUID,
	dataLayerId: UUID,
): Promise<AerialPlotResponse<UUID[]>> {
	const url = `${apApiBaseUrl}${FACTORS_PATH}GetDataLayerFieldAssociations?dataLayerId=${dataLayerId}&clientId=${clientId}`;
	const fetchOptions = {
		method: "GET",
		headers: { Authorization: `Bearer ${accessToken}` },
	};
	const response = await fetch(url, fetchOptions);
	const body = await response.json();
	if (!response.ok) {
		// Throw error on HTTP unsuccessful response codes (>299) per react-query guidelines.
		const apr = body as AerialPlotResponse<any>;
		throw new ApiError(apr?.statusDescription ?? "GetDataLayerFieldAssociations failed", response.status, apr);
	}
	return body as AerialPlotResponse<UUID[]>;
}

/**
 * Create data layer field association.
 */
async function createDataLayerFieldAssociation({
	getAccessToken,
	clientId,
	dataLayerId,
	fieldId,
}: {
	getAccessToken: AccessTokenProvider;
	clientId: UUID;
	dataLayerId: UUID;
	fieldId: UUID;
}): Promise<AerialPlotResponse<DataLayer>> {
	const accessToken = await getAccessToken();
	const url = `${apApiBaseUrl}${FACTORS_PATH}CreateDataLayerFieldAssociation?clientId=${clientId}&dataLayerId=${dataLayerId}&fieldId=${fieldId}`;
	const fetchOptions = {
		method: "POST",
		headers: { Authorization: `Bearer ${accessToken}` },
	};
	const response = await fetch(url, fetchOptions);
	const body = await response.json();
	if (!response.ok) {
		// Throw error on HTTP unsuccessful response codes (>299) per react-query guidelines.
		const apr = body as AerialPlotResponse<any>;
		throw new ApiError(apr?.statusDescription ?? "Create dataLayer association failed", response.status, apr);
	}
	return body as AerialPlotResponse<DataLayer>;
}

/**
 * Delete data layer field association.
 */
async function deleteDataLayerFieldAssociation({
	getAccessToken,
	clientId,
	dataLayerId,
	fieldId,
}: {
	getAccessToken: AccessTokenProvider;
	clientId: UUID;
	dataLayerId: UUID;
	fieldId: UUID;
}): Promise<AerialPlotResponse<DataLayer>> {
	const accessToken = await getAccessToken();
	const url = `${apApiBaseUrl}${FACTORS_PATH}DeleteDataLayerFieldAssociation?clientId=${clientId}&dataLayerId=${dataLayerId}&fieldId=${fieldId}`;
	const fetchOptions = {
		method: "DELETE",
		headers: { Authorization: `Bearer ${accessToken}` },
	};
	const response = await fetch(url, fetchOptions);
	const body = await response.json();
	if (!response.ok) {
		// Throw error on HTTP unsuccessful response codes (>299) per react-query guidelines.
		const apr = body as AerialPlotResponse<any>;
		throw new ApiError(apr?.statusDescription ?? "Delete dataLayer association failed", response.status, apr);
	}
	return body as AerialPlotResponse<DataLayer>;
}

/**
 * Update an existing data layer. Throws Error on failure, to be react-query compatible.
 */
async function updateDataLayer({
	getAccessToken,
	request,
}: {
	getAccessToken: AccessTokenProvider;
	request: DataLayer;
}): Promise<AerialPlotResponse<DataLayer>> {
	const accessToken = await getAccessToken();
	const url = `${apApiBaseUrl}${DATA_LAYERS_PATH}`;
	const fetchOptions = {
		method: "PUT",
		headers: {
			Authorization: `Bearer ${accessToken}`,
			"Content-Type": "application/json",
		},
		body: JSON.stringify(request),
	};
	const response = await fetch(url, fetchOptions);
	const body = await response.json();
	if (!response.ok) {
		// handle HTTP unsuccessful response codes (>299)
		const apr = body as AerialPlotResponse<any>;
		throw new ApiError(apr?.statusDescription ?? "Update DataLayer failed", response.status, apr);
	}
	return body as AerialPlotResponse<DataLayer>;
}

// ===== Data Platform API - Data Layer Uploads and Workflows Endpoints =====

/**
 * Create a new vector data layer file upload using XMLHttpRequest, with progress callback support.
 * Inserts into DataLayerUploads.
 */
function createVectorDataLayerUploadWithProgress({
	getAccessToken,
	clientId,
	request,
	onProgress,
	signal,
}: {
	getAccessToken: AccessTokenProvider;
	clientId: UUID;
	request: CreateDataLayerUploadRequest;
	onProgress?: (event: ProgressEvent) => void;
	signal?: AbortSignal;
}): Promise<AerialPlotResponse<VectorDataLayerUploadPreprocResult>> {
	return new Promise(async (resolve, reject) => {
		let accessToken: string;
		try {
			if (signal?.aborted) {
				reject(new DOMException("Upload cancelled", "AbortError"));
				return;
			}
			accessToken = await getAccessToken();
		} catch (err) {
			reject(err);
			return;
		}

		const url = `${apDataPlatformBaseUrl}${DP_DATA_LAYERS_PATH}Vectors?clientId=${clientId}`;
		const formData = new FormData();
		formData.append("dataLayerId", request.dataLayerId);
		formData.append("dataLayerTypeId", request.dataLayerTypeId);
		formData.append("file", request.file);

		const xhr = new XMLHttpRequest();
		xhr.open("POST", url, true);
		xhr.setRequestHeader("Authorization", `Bearer ${accessToken}`);

		// Handle abort signal
		if (signal) {
			if (signal.aborted) {
				reject(new DOMException("Upload cancelled", "AbortError"));
				return;
			}
			signal.addEventListener("abort", () => {
				xhr.abort();
				reject(new DOMException("Upload cancelled", "AbortError"));
			});
		}

		xhr.onabort = () => {
			reject(new DOMException("Upload cancelled", "AbortError"));
		};
		xhr.onerror = () => {
			reject(new Error("Network error during upload (possible CORS issue)"));
		};
		xhr.onload = () => {
			if (xhr.status >= 200 && xhr.status < 300) {
				const response = JSON.parse(xhr.responseText);
				resolve(response);
			} else {
				const body: AerialPlotResponse<any> = JSON.parse(xhr.responseText);
				reject(new ApiError(body?.statusDescription ?? "Upload to Vectors failed", xhr.status, body));
			}
		};
		if (onProgress && xhr.upload) {
			xhr.upload.onprogress = onProgress;
		}
		xhr.send(formData);
	});
}

/**
 * Create a new source raster data layer file upload using XMLHttpRequest, with progress callback support.
 * Inserts into DataLayerUploads.
 */
function createRasterDataLayerUploadWithProgress({
	getAccessToken,
	clientId,
	request,
	onProgress,
	signal,
}: {
	getAccessToken: AccessTokenProvider;
	clientId: UUID;
	request: CreateDataLayerUploadRequest;
	onProgress?: (event: ProgressEvent) => void;
	signal?: AbortSignal;
}): Promise<AerialPlotResponse<DataLayerUpload>> {
	return new Promise(async (resolve, reject) => {
		let accessToken: string;
		try {
			if (signal?.aborted) {
				reject(new DOMException("Upload cancelled", "AbortError"));
				return;
			}
			accessToken = await getAccessToken();
		} catch (err) {
			reject(err);
			return;
		}

		const url = `${apDataPlatformBaseUrl}${DP_DATA_LAYERS_PATH}Rasters?clientId=${clientId}`;
		const formData = new FormData();
		formData.append("dataLayerId", request.dataLayerId);
		formData.append("dataLayerTypeId", request.dataLayerTypeId);
		formData.append("file", request.file);

		const xhr = new XMLHttpRequest();
		xhr.open("POST", url, true);
		xhr.setRequestHeader("Authorization", `Bearer ${accessToken}`);

		// Handle abort signal
		if (signal) {
			if (signal.aborted) {
				reject(new DOMException("Upload cancelled", "AbortError"));
				return;
			}
			signal.addEventListener("abort", () => {
				xhr.abort();
				reject(new DOMException("Upload cancelled", "AbortError"));
			});
		}

		xhr.onabort = () => {
			reject(new DOMException("Upload cancelled", "AbortError"));
		};
		xhr.onerror = () => {
			reject(new Error("Network error during upload (possible CORS issue)"));
		};
		xhr.onload = () => {
			if (xhr.status >= 200 && xhr.status < 300) {
				const response = JSON.parse(xhr.responseText);
				resolve(response);
			} else {
				const body: AerialPlotResponse<any> = JSON.parse(xhr.responseText);
				reject(new ApiError(body?.statusDescription ?? "Upload to Rasters failed", xhr.status, body));
			}
		};
		if (onProgress && xhr.upload) {
			xhr.upload.onprogress = onProgress;
		}
		xhr.send(formData);
	});
}

/**
 * Query options for fetching a list of DataLayerUploads for a DataLayer Id.
 * Leverages queryOptions per react-query guidelines
 * https://tanstack.com/query/latest/docs/framework/react/guides/query-options
 */
function vectorDataLayerUploadsQueryOptions(getAccessToken: AccessTokenProvider, clientId: UUID, dataLayerId?: UUID) {
	return queryOptions<
		DataLayerUpload[],
		Error,
		DataLayerUpload[],
		["Vectors/GetUploadsForDataLayer", UUID, UUID | undefined]
	>({
		enabled: !!dataLayerId,
		queryKey: ["Vectors/GetUploadsForDataLayer", clientId, dataLayerId],
		queryFn: async () => {
			const accessToken = await getAccessToken();
			const responseModel = await _getVectorDataLayerUploads(accessToken, clientId, dataLayerId!);
			// extract the data model and enhance the date properties
			return responseModel.data.map((upload) => ({
				...upload,
				createdDateTimeUtc: upload.createdDateTimeUtc
					? moment.utc(upload.createdDateTimeUtc).toDate()
					: upload.createdDateTimeUtc,
			}));
		},
	});
}

async function _getVectorDataLayerUploads(
	accessToken: string,
	clientId: UUID,
	dataLayerId: UUID,
): Promise<AerialPlotResponse<DataLayerUpload[]>> {
	const url = `${apDataPlatformBaseUrl}${DP_DATA_LAYERS_PATH}Vectors/GetUploadsForDataLayer?dataLayerId=${dataLayerId}&clientId=${clientId}`;
	const fetchOptions = {
		method: "GET",
		headers: {
			Authorization: `Bearer ${accessToken}`,
		},
	};
	const response = await fetch(url, fetchOptions);
	const body = await response.json();
	if (!response.ok) {
		// Throw error on HTTP unsuccessful response codes (>299) per react-query guidelines.
		const apr: AerialPlotResponse<any> = body;
		throw new ApiError(apr?.statusDescription ?? "GetUploadsForDataLayer failed", response.status, apr);
	}
	return body as AerialPlotResponse<DataLayerUpload[]>;
}

/**
 * Update a vector data layer upload with configuration and processing request.
 */
async function updateVectorDataLayerUpload({
	getAccessToken,
	clientId,
	request,
}: {
	getAccessToken: AccessTokenProvider;
	clientId: UUID;
	request: VectorDataLayerRequest;
}): Promise<AerialPlotResponse<VectorDataLayerUploadStdResult>> {
	const accessToken = await getAccessToken();
	const url = `${apDataPlatformBaseUrl}${DP_DATA_LAYERS_PATH}Vectors?clientId=${clientId}`;
	const fetchOptions = {
		method: "PUT",
		headers: {
			Authorization: `Bearer ${accessToken}`,
			"Content-Type": "application/json",
		},
		body: JSON.stringify(request),
	};
	const response = await fetch(url, fetchOptions);
	const body = await response.json();
	if (!response.ok) {
		// Throw error on HTTP unsuccessful response codes (>299) per react-query guidelines.
		const apr: AerialPlotResponse<any> = body;
		throw new ApiError(apr?.statusDescription ?? "Update DataLayerUpload failed", response.status, apr);
	}
	return body as AerialPlotResponse<VectorDataLayerUploadStdResult>;
}

/**
 * Delete a vector data layer upload, and it's associated file assets.
 */
async function deleteVectorDataLayerUpload({
	getAccessToken,
	clientId,
	dataLayerUploadId,
}: {
	getAccessToken: AccessTokenProvider;
	clientId: UUID;
	dataLayerUploadId: UUID;
}): Promise<AerialPlotResponse<void>> {
	const accessToken = await getAccessToken();
	const url = `${apDataPlatformBaseUrl}${DP_DATA_LAYERS_PATH}Vectors?uploadId=${dataLayerUploadId}&clientId=${clientId}`;
	const fetchOptions = {
		method: "DELETE",
		headers: { Authorization: `Bearer ${accessToken}` },
	};
	const response = await fetch(url, fetchOptions);
	const body = await response.json();
	if (!response.ok) {
		// Throw error on HTTP unsuccessful response codes (>299) per react-query guidelines.
		const apr = body as AerialPlotResponse<any>;
		throw new ApiError(apr?.statusDescription ?? "Delete Vectors DataLayerUpload failed", response.status, apr);
	}
	return body as AerialPlotResponse<void>;
}

/**
 * Delete a raster data layer upload, and it's associated file assets.
 */
async function deleteRasterDataLayerUpload({
	getAccessToken,
	clientId,
	dataLayerUploadId,
}: {
	getAccessToken: AccessTokenProvider;
	clientId: UUID;
	dataLayerUploadId: UUID;
}): Promise<AerialPlotResponse<void>> {
	const accessToken = await getAccessToken();
	const url = `${apDataPlatformBaseUrl}${DP_DATA_LAYERS_PATH}Rasters?uploadId=${dataLayerUploadId}&clientId=${clientId}`;
	const fetchOptions = {
		method: "DELETE",
		headers: { Authorization: `Bearer ${accessToken}` },
	};
	const response = await fetch(url, fetchOptions);
	const body = await response.json();
	if (!response.ok) {
		// Throw error on HTTP unsuccessful response codes (>299) per react-query guidelines.
		const apr = body as AerialPlotResponse<any>;
		throw new ApiError(apr?.statusDescription ?? "Delete Rasters DataLayerUpload failed", response.status, apr);
	}
	return body as AerialPlotResponse<void>;
}

/**
 * Query options for Searching for field associations by a DataLayer.Id
 * Leverages queryOptions per react-query guidelines
 * https://tanstack.com/query/latest/docs/framework/react/guides/query-options
 */
function searchFieldsByVectorDataLayerQueryOptions(
	getAccessToken: AccessTokenProvider,
	clientId: UUID,
	dataLayerId?: UUID,
) {
	return queryOptions<
		FieldWithBoundaryAndPlots[],
		Error,
		FieldWithBoundaryAndPlots[],
		["SearchFieldsByDataLayer", UUID, UUID | undefined]
	>({
		enabled: !!dataLayerId && !!clientId,
		queryKey: ["SearchFieldsByDataLayer", clientId, dataLayerId],
		queryFn: async () => {
			const accessToken = await getAccessToken();
			const responseModel = await _searchFieldsByVectorDataLayer(accessToken, clientId, dataLayerId!);
			// extract the data model and enhance the date properties
			return responseModel.data.map((field) => ({
				...field,
				flightCreatedDateTimeUtc: field.flightCreatedDateTimeUtc
					? moment.utc(field.flightCreatedDateTimeUtc).toDate()
					: field.flightCreatedDateTimeUtc,
			}));
		},
	});
}

async function _searchFieldsByVectorDataLayer(
	accessToken: string,
	clientId: UUID,
	dataLayerId: UUID,
): Promise<AerialPlotResponse<FieldWithBoundaryAndPlots[]>> {
	const url = `${apDataPlatformBaseUrl}${DP_DATA_LAYERS_PATH}Vectors/SearchFieldsByDataLayer?dataLayerId=${dataLayerId}&clientId=${clientId}`;
	const fetchOptions = {
		method: "GET",
		headers: {
			Authorization: `Bearer ${accessToken}`,
		},
	};
	const response = await fetch(url, fetchOptions);
	const body = await response.json();
	if (!response.ok) {
		// Throw error on HTTP unsuccessful response codes (>299) per react-query guidelines.
		const apr: AerialPlotResponse<any> = body;
		throw new ApiError(apr?.statusDescription ?? "SearchFieldsByDataLayer failed", response.status, apr);
	}
	return body as AerialPlotResponse<FieldWithBoundaryAndPlots[]>;
}

/**
 * Query options for fetching a list of Raster DataLayerUploads for a DataLayer Id.
 * Leverages queryOptions per react-query guidelines
 * https://tanstack.com/query/latest/docs/framework/react/guides/query-options
 */
function rasterDataLayerUploadsQueryOptions(getAccessToken: AccessTokenProvider, clientId: UUID, dataLayerId?: UUID) {
	return queryOptions<
		DataLayerUpload[],
		Error,
		DataLayerUpload[],
		["GetRasterDataLayerUploads", UUID, UUID | undefined]
	>({
		enabled: !!dataLayerId,
		queryKey: ["GetRasterDataLayerUploads", clientId, dataLayerId],
		queryFn: async () => {
			const accessToken = await getAccessToken();
			const responseModel = await _getRasterDataLayerUploads(accessToken, clientId, dataLayerId!);
			// extract the data model and enhance the date properties
			return responseModel.data.map((upload) => ({
				...upload,
				createdDateTimeUtc: upload.createdDateTimeUtc
					? moment.utc(upload.createdDateTimeUtc).toDate()
					: upload.createdDateTimeUtc,
			}));
		},
	});
}

async function _getRasterDataLayerUploads(
	accessToken: string,
	clientId: UUID,
	dataLayerId: UUID,
): Promise<AerialPlotResponse<DataLayerUpload[]>> {
	const url = `${apDataPlatformBaseUrl}${DP_DATA_LAYERS_PATH}Rasters/GetUploadsForDataLayer?dataLayerId=${dataLayerId}&clientId=${clientId}`;
	const fetchOptions = {
		method: "GET",
		headers: {
			Authorization: `Bearer ${accessToken}`,
		},
	};
	const response = await fetch(url, fetchOptions);
	const body = await response.json();
	if (!response.ok) {
		// Throw error on HTTP unsuccessful response codes (>299) per react-query guidelines.
		const apr: AerialPlotResponse<any> = body;
		throw new ApiError(apr?.statusDescription ?? "Rasters/GetUploadsForDataLayer failed", response.status, apr);
	}
	return body as AerialPlotResponse<DataLayerUpload[]>;
}

async function runPlotAssociations({
	getAccessToken,
	clientId,
	dataLayerId,
}: {
	getAccessToken: AccessTokenProvider;
	clientId: UUID;
	dataLayerId: UUID;
}): Promise<AerialPlotResponse<PlotResultsInfo>> {
	const accessToken = await getAccessToken();
	const url = `${apDataPlatformBaseUrl}${DP_DATA_LAYERS_PATH}Vectors/RunPlotAssociations?dataLayerId=${dataLayerId}&clientId=${clientId}`;
	const fetchOptions = {
		method: "POST",
		headers: {
			Authorization: `Bearer ${accessToken}`,
		},
	};
	const response = await fetch(url, fetchOptions);
	const body = await response.json();
	if (!response.ok) {
		const apr: AerialPlotResponse<any> = body;
		throw new ApiError(apr?.statusDescription ?? "RunPlotAssociations failed", response.status, apr);
	}

	return body as AerialPlotResponse<PlotResultsInfo>;
}

async function deletePlotAssociations({
	getAccessToken,
	clientId,
	dataLayerId,
}: {
	getAccessToken: AccessTokenProvider;
	clientId: UUID;
	dataLayerId: UUID;
}): Promise<AerialPlotResponse<void>> {
	const accessToken = await getAccessToken();
	const url = `${apDataPlatformBaseUrl}${DP_DATA_LAYERS_PATH}Vectors/DeletePlotAssociations?dataLayerId=${dataLayerId}&clientId=${clientId}`;
	const fetchOptions = {
		method: "DELETE",
		headers: {
			Authorization: `Bearer ${accessToken}`,
		},
	};
	const response = await fetch(url, fetchOptions);
	const body = await response.json();
	if (!response.ok) {
		const apr: AerialPlotResponse<any> = body;
		throw new ApiError(apr?.statusDescription ?? "DeletePlotAssociations failed", response.status, apr);
	}

	return body as AerialPlotResponse<void>;
}

function plotResultsInfoQueryOptions(getAccessToken: AccessTokenProvider, clientId: UUID, dataLayerId: UUID) {
	return queryOptions<PlotResultsInfo, Error>({
		enabled: !!dataLayerId,
		queryKey: ["GetDataLayerPlotAssociationCounts", clientId, dataLayerId],
		initialData: [[], {}],
		queryFn: async () => {
			const accessToken = await getAccessToken();
			const responseModel = await _getPlotResultsInfo(accessToken, clientId, dataLayerId);

			return responseModel.data;
		},
	});
}

async function _getPlotResultsInfo(
	accessToken: string,
	clientId: UUID,
	dataLayerId: UUID,
): Promise<AerialPlotResponse<PlotResultsInfo>> {
	const url = `${apDataPlatformBaseUrl}${DP_DATA_LAYERS_PATH}Vectors/GetPlotResultsInfo?dataLayerId=${dataLayerId}&clientId=${clientId}`;
	const fetchOptions = {
		method: "GET",
		headers: {
			Authorization: `Bearer ${accessToken}`,
		},
	};
	const response = await fetch(url, fetchOptions);
	const body = await response.json();
	if (!response.ok) {
		const apr: AerialPlotResponse<any> = body;
		throw new ApiError(apr?.statusDescription ?? "GetPlotResultsInfo failed", response.status, apr);
	}

	return body as AerialPlotResponse<PlotResultsInfo>;
}

function dataLayersDropdownQueryOptions(getAccessToken: AccessTokenProvider, clientId: UUID, trialId: UUID) {
	return queryOptions<DataLayerDropdownInfo[], Error>({
		enabled: !!trialId,
		queryKey: ["GetDataLayersDropdown", trialId, clientId],
		initialData: [],
		queryFn: async () => {
			const accessToken = await getAccessToken();
			const responseModel = await _getDataLayersDropdown(accessToken, clientId, trialId);

			return responseModel.data;
		},
	});
}

async function _getDataLayersDropdown(
	accessToken: string,
	clientId: UUID,
	trialId: UUID,
): Promise<AerialPlotResponse<DataLayerDropdownInfo[]>> {
	const url = `${apDataPlatformBaseUrl}${DP_DATA_LAYERS_PATH}Vectors/GetDataLayersDropdown?trialId=${trialId}&clientId=${clientId}`;
	const fetchOptions = {
		method: "GET",
		headers: {
			Authorization: `Bearer ${accessToken}`,
		},
	};
	const response = await fetch(url, fetchOptions);
	const body = await response.json();
	if (!response.ok) {
		const apr: AerialPlotResponse<any> = body;
		throw new ApiError(apr?.statusDescription ?? "GetDataLayersDropdown failed", response.status, apr);
	}

	return body as AerialPlotResponse<DataLayerDropdownInfo[]>;
}

export {
	// queries
	dataLayerDetailsQueryOptions,
	dataLayerQueryOptions,
	vectorDataLayerUploadsQueryOptions,
	rasterDataLayerUploadsQueryOptions,
	searchFieldsByVectorDataLayerQueryOptions,
	dataLayerFieldAssociationsQueryOptions,
	plotResultsInfoQueryOptions,
	dataLayersDropdownQueryOptions,

	// helpers
	fetchFromAzureFileShare,

	// mutation fns
	createDataLayer,
	updateDataLayer,
	deleteDataLayer,
	createVectorDataLayerUploadWithProgress,
	createRasterDataLayerUploadWithProgress,
	deleteVectorDataLayerUpload,
	deleteRasterDataLayerUpload,
	updateVectorDataLayerUpload,
	createDataLayerFieldAssociation,
	deleteDataLayerFieldAssociation,
	runPlotAssociations,
	deletePlotAssociations,
};
